import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users2, Save, Trash2, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

// Clubs: reusable saved rosters on the organizer's account. Load a club's members
// into the current session in one tap, or save the current roster as a new club.
export default function ClubsModal({ open, onClose, players = [], onLoad }) {
  const [clubs, setClubs] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/openplay/clubs").then(({ data }) => setClubs(data)).catch(() => setClubs([]));
  useEffect(() => { if (open) { setClubs(null); load(); } }, [open]);

  const saveCurrent = () => {
    const nm = name.trim();
    if (!nm) { toast.error("Name your club first."); return; }
    if (!players.length) { toast.error("Add some players to the roster before saving a club."); return; }
    setSaving(true);
    const members = players.map((p) => ({ name: p.name, skillLevel: p.skillLevel || 3 }));
    api.post("/openplay/clubs", { name: nm, members })
      .then(() => { toast.success(`Club "${nm}" saved (${members.length} players).`); setName(""); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save club."))
      .finally(() => setSaving(false));
  };

  const loadClub = (c) => {
    const added = onLoad(c.members || []);
    toast.success(`Loaded ${added} player${added === 1 ? "" : "s"} from "${c.name}".`);
    onClose();
  };

  const del = (c) => {
    api.delete(`/openplay/clubs/${c.club_id}`).then(() => { toast.success(`Deleted "${c.name}".`); load(); })
      .catch(() => toast.error("Could not delete club."));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass max-w-lg" style={{ background: "var(--surface)" }} data-testid="clubs-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Users2 className="w-5 h-5 text-primary-c" /> Clubs</DialogTitle>
          <DialogDescription style={{ color: "var(--text-2)" }}>Save a reusable roster and load it into any session with one tap.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-2">
          <Input data-testid="club-name-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="New club name (saves current roster)" className="bg-surface glow-border focus:ring-2 focus:ring-lime-400" />
          <button data-testid="club-save-current" onClick={saveCurrent} disabled={saving}
            className="btn-glow rounded-md px-4 font-bold flex items-center gap-2 shrink-0 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
        <p className="text-[11px]" style={{ color: "var(--text-2)" }}>Saves the {players.length} player{players.length === 1 ? "" : "s"} currently on your roster.</p>

        <div className="mt-2 max-h-72 overflow-y-auto space-y-2" data-testid="clubs-list">
          {clubs === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>
          ) : clubs.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: "var(--text-2)" }} data-testid="clubs-empty">No saved clubs yet. Build a roster and save it above.</p>
          ) : clubs.map((c) => (
            <div key={c.club_id} className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5" style={{ background: "var(--bg)" }} data-testid={`club-row-${c.club_id}`}>
              <div className="min-w-0">
                <div className="font-bold truncate">{c.name}</div>
                <div className="text-[11px] truncate" style={{ color: "var(--text-2)" }}>{c.members.length} players · {c.members.slice(0, 4).map((m) => m.name).join(", ")}{c.members.length > 4 ? "…" : ""}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`club-load-${c.club_id}`} onClick={() => loadClub(c)}
                  className="btn-glow rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Load</button>
                <button data-testid={`club-delete-${c.club_id}`} onClick={() => del(c)} aria-label="Delete club" className="p-1.5 hover:scale-110 transition-transform">
                  <Trash2 className="w-4 h-4" style={{ color: "var(--text-2)" }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
